import Styles from './Message.module.scss'
import cx from 'classnames'
import { MessageContent } from './Content/MessageContent'
import { MessageProps, PollingMessageProps } from './Types'
import { usePollMessage } from 'hooks/queries/chat/usePollMessage'
import { AIJobStatus } from 'types'
import { ErrorMessage } from './ErrorMessage'
import { LastMessageActionsV2 } from './LastMessageActionsV2'
import StarRating from './StarRating'
import { Text } from 'blaze-ui/Text/Text'

export const formatMessageTime = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
}

const Message = ({ message, isLastMessage, isNextGen, context, isDisabled }: MessageProps) => {
  const isProcessing = message.status && [AIJobStatus.Pending, AIJobStatus.Started].includes(message.status)
  let content = message.content

  if (isProcessing && content.length === 0) {
    content = [...content, { type: 'processing' }]
  }

  if (!content.length) return null

  const filteredContent = isLastMessage ? content : content.filter((c) => c.type !== 'suggested_followups')
  const visibleContent = [
    ...filteredContent.filter((c) => c.type !== 'credit_usage'),
    ...filteredContent.filter((c) => c.type === 'credit_usage'),
  ]

  const imageContent = visibleContent.filter((c) => c.type === 'image')
  const otherContent = visibleContent.filter((c) => c.type !== 'image')
  const hasMixedContent = message.role === 'user' && imageContent.length > 0 && otherContent.length > 0

  if (hasMixedContent) {
    const mixedElements = (
      <div className={Styles.splitMessage} data-testid={`message-${message.id}`}>
        <div className={Styles.message}>
          {imageContent.map((item, id) => (
            <MessageContent
              key={`${message.id}-img-${id}`}
              content={item}
              message={message}
              isNextGen={isNextGen}
              context={context}
              isDisabled={isDisabled}
            />
          ))}
        </div>
        <div className={cx(Styles.message, Styles[message.role])} data-role={message.role}>
          {otherContent.map((item, id) => (
            <MessageContent
              key={`${message.id}-${id}`}
              content={item}
              message={message}
              isNextGen={isNextGen}
              context={context}
              isDisabled={isDisabled}
            />
          ))}
        </div>
      </div>
    )

    if (isNextGen) {
      return (
        <div className={Styles.userMessageWrapper}>
          {mixedElements}
          <Text variant="metadata" color="var(--dark-40)">
            {formatMessageTime(message.created_at)}
          </Text>
        </div>
      )
    }

    return mixedElements
  }

  const showLastMessageActions =
    !isProcessing &&
    isLastMessage &&
    message.role === 'assistant' &&
    !message.content.some((content) => content.type === 'unsupported_request') &&
    !(isNextGen && message.content.some((content) => content.type === 'comparison_resolved'))

  const messageElement = (
    <div
      className={cx(Styles.message, Styles[message.role])}
      data-testid={`message-${message.id}`}
      data-role={message.role}
    >
      {visibleContent.map((item, id) => (
        <MessageContent
          key={`${message.id}-${id}`}
          content={item}
          message={message}
          isNextGen={isNextGen}
          context={context}
          isDisabled={isDisabled}
        />
      ))}
      {showLastMessageActions && isNextGen && <StarRating message={message} />}
      {showLastMessageActions && !isNextGen && <LastMessageActionsV2 message={message} />}
    </div>
  )

  if (isNextGen && message.role === 'user') {
    return (
      <div className={Styles.userMessageWrapper}>
        {messageElement}
        <Text variant="metadata" color="var(--dark-40)">
          {formatMessageTime(message.created_at)}
        </Text>
      </div>
    )
  }

  return messageElement
}

const PollingMessage = ({ threadId, id, isLastMessage, isNextGen }: PollingMessageProps) => {
  const { data: messageData } = usePollMessage({ threadId, id })

  if (!messageData) {
    return null
  }

  return <Message message={messageData.message} isLastMessage={isLastMessage} isNextGen={isNextGen} />
}

const MessageContainer = ({ message, isLastMessage, isNextGen, context, isDisabled }: MessageProps) => {
  if (message.status && [AIJobStatus.Pending, AIJobStatus.Started].includes(message.status)) {
    return <PollingMessage threadId={message.thread_id} id={message.id} isNextGen={isNextGen} />
  }

  if (message.status === AIJobStatus.Failed) {
    return <ErrorMessage message={message} />
  }

  return (
    <Message
      message={message}
      isLastMessage={isLastMessage}
      isNextGen={isNextGen}
      context={context}
      isDisabled={isDisabled}
    />
  )
}

export { MessageContainer as Message }
